import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import dayjs from 'dayjs';
import { Asset, AssetStatus } from '@core/generated/gql-types';
import { useAssetStore } from '@core/asset/useAssetStore';
import {
  getMetadataDateMs,
  parseAssetMetadata,
} from '@core/util/assetMetadata.util';
import { useTenantId } from '@/contexts/TenantContext';
import { downloadFileFromUrl } from '@/utils/download.util';
import DocumentPreviewModal from '@/components/worker/documents/DocumentPreviewModal';

const HIDDEN_ASSET_STATUSES = new Set<AssetStatus>([AssetStatus.Replaced]);

const formatApplicantDocumentDate = (asset: Asset): string | null => {
  const meta = parseAssetMetadata(asset.metadata);
  const expiryMs = getMetadataDateMs(meta, [
    'expiryDate',
    'expiresAt',
    'expirationDate',
  ]);
  const issueMs = getMetadataDateMs(meta, ['issueDate', 'issuedAt']);
  const displayMs = expiryMs ?? issueMs;

  if (displayMs == null) {
    return null;
  }

  return dayjs(displayMs).format('DD/MM/YYYY');
};

const ApplicantDocumentsSection = ({ userId }: { userId: string }) => {
  const tenantId = useTenantId();
  const {
    fetchAssetsForUser,
    getAssetsForUser,
    fetchAssetTemplatesForTenant,
    getAssetTemplatesForTenant,
  } = useAssetStore();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string | null>(null);

  const assets = getAssetsForUser(userId);
  const assetTemplates = useMemo(
    () => (tenantId ? getAssetTemplatesForTenant(tenantId) : []),
    [tenantId, getAssetTemplatesForTenant],
  );

  const templateTitleByTag = useMemo(() => {
    return new Map(
      assetTemplates.map((template) => [template.tag, template.title]),
    );
  }, [assetTemplates]);

  const visibleAssets = useMemo(() => {
    return assets.filter(
      (asset) =>
        asset.status == null || !HIDDEN_ASSET_STATUSES.has(asset.status),
    );
  }, [assets]);

  const getDocumentTitle = useCallback(
    (asset: Asset) => templateTitleByTag.get(asset.tag) ?? asset.tag,
    [templateTitleByTag],
  );

  const loadDocuments = useCallback(async () => {
    if (!userId || hasFetched) {
      return;
    }

    setIsLoading(true);
    try {
      await fetchAssetsForUser(userId);
      if (tenantId) {
        await fetchAssetTemplatesForTenant(tenantId);
      }
      setHasFetched(true);
    } catch (error) {
      console.error('Error loading applicant documents:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    userId,
    hasFetched,
    tenantId,
    fetchAssetsForUser,
    fetchAssetTemplatesForTenant,
  ]);

  useEffect(() => {
    if (!isCollapsed) {
      void loadDocuments();
    }
  }, [isCollapsed, loadDocuments]);

  const handleToggleCollapse = () => {
    setIsCollapsed((previous) => !previous);
  };

  const handlePreview = (asset: Asset) => {
    setPreviewAsset(asset);
    setPreviewTitle(getDocumentTitle(asset));
  };

  const handleDownload = async (asset: Asset) => {
    if (!asset.url) {
      return;
    }

    const fileName =
      asset.key?.split('/')?.at(-1) || getDocumentTitle(asset) || 'document';
    await downloadFileFromUrl(asset.url, fileName);
  };

  return (
    <>
      <div className="mt-4">
        <button
          type="button"
          onClick={handleToggleCollapse}
          className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-gray-900"
        >
          <span className="text-gray-500">
            {isCollapsed ? (
              <ChevronRightIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </span>
          Documents
        </button>

        {!isCollapsed && (
          <div className="mt-3">
            {isLoading ? (
              <p className="text-sm text-gray-500">Loading documents...</p>
            ) : visibleAssets.length === 0 ? (
              <p className="text-sm text-gray-500">No documents available</p>
            ) : (
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[35%]" />
                  <col className="w-[20%]" />
                  <col />
                  <col className="w-52" />
                </colgroup>
                <thead>
                  <tr className="text-left text-sm font-semibold text-gray-900">
                    <th className="pr-4 pb-2 font-semibold">Document</th>
                    <th className="pr-4 pb-2 font-semibold">
                      Issue/Expiry date
                    </th>
                    <th className="pb-2" />
                    <th className="pb-2" />
                  </tr>
                </thead>
                <tbody>
                  {visibleAssets.map((asset) => {
                    const dateDisplay = formatApplicantDocumentDate(asset);
                    const title = getDocumentTitle(asset);

                    return (
                      <tr key={asset.id}>
                        <td className="py-2 pr-4 text-sm text-gray-900">
                          {title}
                        </td>
                        <td className="py-2 pr-4 text-sm text-gray-600">
                          {dateDisplay ?? '-'}
                        </td>
                        <td className="py-2" />
                        <td className="py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handlePreview(asset)}
                              className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              <FontAwesomeIcon
                                icon={faEye}
                                className="h-3.5 w-3.5"
                              />
                              Preview
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleDownload(asset)}
                              className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              <FontAwesomeIcon
                                icon={faDownload}
                                className="h-3.5 w-3.5"
                              />
                              Download
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <DocumentPreviewModal
        assetPreview={{
          previewAsset,
          setPreviewAsset,
          previewTitle,
          setPreviewTitle,
        }}
      />
    </>
  );
};

export default ApplicantDocumentsSection;
